export const FIELD_APP_HOME_PATH = '/today'

const FIELD_APP_HOST = 'app.ascsurvey.com'
const PRODUCTION_CONSOLE_HOST = 'ascsurvey.com'
const STAGING_FIELD_APP_HOST = 'app.staging.ascsurvey.com'
const STAGING_CONSOLE_HOST = 'staging.ascsurvey.com'
const WWW_CONSOLE_HOST = 'www.ascsurvey.com'

/**
 * Hosts treated as "field app" when the build is not production.
 * Mirrors the seed-login allowlist in `Login.tsx` so local-device HITL
 * runs (cloudflared tunnels, LAN IPs) render the field-app 3-card
 * chooser instead of the deprecated console KPI view. Production MUST
 * never treat these as field-app hosts.
 */
const DEV_FIELD_APP_HOST_PATTERNS: ReadonlyArray<RegExp> = [
  /^localhost$/,
  /^127\.0\.0\.1$/,
  /^192\.168\.[0-9]+\.[0-9]+$/,
  /^10\.[0-9]+\.[0-9]+\.[0-9]+$/,
  /^172\.(1[6-9]|2[0-9]|3[0-1])\.[0-9]+\.[0-9]+$/,
  /\.trycloudflare\.com$/,
  /\.ngrok\.io$/,
  /\.ngrok-free\.app$/,
]

const APP_HOST_NORMAL_HOSTS: Record<string, string> = {
  [STAGING_FIELD_APP_HOST]: STAGING_CONSOLE_HOST,
  [FIELD_APP_HOST]: PRODUCTION_CONSOLE_HOST,
}

const NORMAL_APP_FIELD_HOSTS: Record<string, string> = {
  [STAGING_CONSOLE_HOST]: STAGING_FIELD_APP_HOST,
  [STAGING_FIELD_APP_HOST]: STAGING_FIELD_APP_HOST,
  [PRODUCTION_CONSOLE_HOST]: FIELD_APP_HOST,
  [FIELD_APP_HOST]: FIELD_APP_HOST,
}

const FIELD_APP_ALLOWED_EXACT_PATHS = new Set([
  '/',
  '/login',
  '/forgot-password',
  '/reset-password',
  '/surveys',
  '/visits',
  '/self-monitoring',
  '/self-monitoring/checklist',
  '/today',
  '/facilities',
  '/facilities/new',
  '/profile',
  '/profile/mfa',
  FIELD_APP_HOME_PATH,
])

const FIELD_APP_ALLOWED_PATTERNS = [
  /^\/surveys\/[^/]+$/,
  /^\/visits\/[^/]+$/,
  /^\/visits\/[^/]+\/survey(?:\/.*)?$/,
  /^\/visits\/[^/]+\/prestudy\/create$/,
  /^\/visits\/[^/]+\/prestudy\/upload$/,
  /^\/visits\/[^/]+\/standard\/[^/]+$/,
  /^\/facilities\/[^/]+$/,
  /^\/facilities\/[^/]+\/contacts\/new$/,
]

function browserLocation(): Location | null {
  return typeof window === 'undefined' ? null : window.location
}

function normalizedEnvironment(): string {
  return (import.meta.env.VITE_ENVIRONMENT as string | undefined)?.toLowerCase() ?? ''
}

function isDevFieldAppHost(hostname: string): boolean {
  const env = normalizedEnvironment()
  if (env === 'production') return false
  // Vitest + jsdom run at hostname 'localhost' and would otherwise match the
  // dev allowlist, causing AppShell to take the field-app redirect branch in
  // unit tests that exercise console-host navigation. Exclude test mode
  // explicitly; localhost during real `npm run dev:lan` still works because
  // MODE is 'development' there, not 'test'.
  //
  // Playwright gate uses --mode e2e on 127.0.0.1:4173. Treating that host as
  // field-app made appHostExternalRouteUrl('/dashboard') return the same URL
  // and AppShell window.location.replace loop forever (blank spinner, no
  // Welcome back heading). Exclude e2e the same way as test.
  if (import.meta.env.MODE === 'test' || import.meta.env.MODE === 'e2e') return false
  return DEV_FIELD_APP_HOST_PATTERNS.some((re) => re.test(hostname))
}

export function isAscSurveyHost(hostname = browserLocation()?.hostname ?? ''): boolean {
  return hostname === PRODUCTION_CONSOLE_HOST
    || hostname === WWW_CONSOLE_HOST
    || hostname.endsWith('.ascsurvey.com')
}

export function shouldEnforceAscHostFamily(hostname = browserLocation()?.hostname ?? ''): boolean {
  const env = normalizedEnvironment()
  if (env !== 'production' && env !== 'staging') return false
  if (hostname.length === 0) return false
  if (isDevFieldAppHost(hostname)) return false
  return true
}

export function resolveUnexpectedHostRedirect(
  location = browserLocation() ?? globalThis.location,
): string | null {
  const hostname = location?.hostname ?? ''
  if (!shouldEnforceAscHostFamily(hostname)) return null
  if (isAscSurveyHost(hostname)) return null

  const appUrl = (import.meta.env.VITE_APP_URL as string | undefined)?.trim()
  if (!appUrl) return null

  try {
    const redirect = new URL(appUrl)
    redirect.pathname = safePath(location.pathname || '/')
    redirect.search = location.search || ''
    redirect.hash = location.hash || ''
    return redirect.toString()
  } catch {
    return null
  }
}

export function isFieldAppHost(hostname = browserLocation()?.hostname ?? ''): boolean {
  return Object.prototype.hasOwnProperty.call(APP_HOST_NORMAL_HOSTS, hostname) || isDevFieldAppHost(hostname)
}

export function isGuidedSurveyPwaHost(hostname = browserLocation()?.hostname ?? ''): boolean {
  return isFieldAppHost(hostname)
}

export function isFieldAppRoutePath(pathname: string): boolean {
  return (
    pathname === FIELD_APP_HOME_PATH ||
    pathname === `${FIELD_APP_HOME_PATH}/` ||
    /^\/visits\/[^/]+\/survey\/walkthrough\/?$/.test(pathname) ||
    pathname === '/self-monitoring/daily' ||
    pathname === '/self-monitoring/daily/'
  )
}

function safePath(pathname: string): string {
  if (!pathname.startsWith('/') || pathname.startsWith('//')) return '/dashboard'
  return pathname
}

export function normalizeFieldAppPath(path: string): string {
  return safePath(path)
}

export function isFieldAppAllowedPath(pathname: string): boolean {
  if (FIELD_APP_ALLOWED_EXACT_PATHS.has(pathname)) return true
  return isFieldAppRoutePath(pathname) || FIELD_APP_ALLOWED_PATTERNS.some((pattern) => pattern.test(pathname))
}

export function isReviewerWorkbenchRoutePath(pathname: string): boolean {
  return pathname === '/reviewer' || /^\/reviewer\/[^/]+\/[^/]+$/.test(pathname)
}

function canStayOnAppHost(pathname: string, viewerRole: string | null): boolean {
  return isReviewerWorkbenchRoutePath(pathname) && (viewerRole === 'Reviewer' || viewerRole === 'Admin')
}

export type FieldAppUrlOptions = {
  /**
   * When set and the host is actually changing, the target URL is tagged
   * with `?resume=1&facility=<name>` so the receiving page can render
   * HostHandoffSplash during the cross-host transition instead of an
   * empty shell.
   */
  resumeFacility?: string | null
}

export function getFieldAppUrl(
  path: string,
  locationOrOptions: Location | FieldAppUrlOptions = browserLocation() ?? globalThis.location,
  maybeOptions?: FieldAppUrlOptions,
): string {
  const isOptions =
    locationOrOptions !== null
    && typeof locationOrOptions === 'object'
    && !('hostname' in locationOrOptions)
  const location = (isOptions
    ? browserLocation() ?? globalThis.location
    : locationOrOptions) as Location
  const options: FieldAppUrlOptions = (isOptions ? locationOrOptions : maybeOptions) ?? {}

  const pathname = normalizeFieldAppPath(path)
  const { protocol, hostname, origin } = location
  const fieldHost = NORMAL_APP_FIELD_HOSTS[hostname]

  const crossHost = !!fieldHost && fieldHost !== hostname
  const resume = crossHost && options.resumeFacility
    ? `?resume=1&facility=${encodeURIComponent(options.resumeFacility)}`
    : ''

  if (fieldHost) {
    return `${protocol}//${fieldHost}${pathname}${resume}`
  }

  return `${origin}${pathname}`
}

export function getConsoleUrl(path = '/dashboard', location = browserLocation() ?? globalThis.location): string {
  const pathname = normalizeFieldAppPath(path)
  const { protocol, hostname, origin } = location
  const consoleHost = APP_HOST_NORMAL_HOSTS[hostname] ?? (hostname === PRODUCTION_CONSOLE_HOST || hostname === STAGING_CONSOLE_HOST ? hostname : null)

  if (consoleHost) {
    return `${protocol}//${consoleHost}${pathname}`
  }

  return `${origin}${pathname}`
}

export function normalAppOriginForHost(
  hostname = browserLocation()?.hostname ?? '',
  protocol = browserLocation()?.protocol ?? 'https:',
  fallbackOrigin = browserLocation()?.origin ?? '',
): string {
  const normalHost = APP_HOST_NORMAL_HOSTS[hostname]
  if (!normalHost) return fallbackOrigin
  return `${protocol}//${normalHost}`
}

export function normalAppUrlForPath(
  pathname = '/dashboard',
  search = '',
  hash = '',
  hostname = browserLocation()?.hostname ?? '',
  protocol = browserLocation()?.protocol ?? 'https:',
  fallbackOrigin = browserLocation()?.origin ?? '',
): string {
  const origin = normalAppOriginForHost(hostname, protocol, fallbackOrigin)
  return `${origin}${safePath(pathname)}${search}${hash}`
}

export function appHostExternalRouteUrl(
  pathname: string,
  search = '',
  hash = '',
  viewerRole: string | null = null,
  hostname = browserLocation()?.hostname ?? '',
  protocol = browserLocation()?.protocol ?? 'https:',
  fallbackOrigin = browserLocation()?.origin ?? '',
): string | null {
  if (!isFieldAppHost(hostname)) return null
  if (pathname === '/' || pathname === '') return FIELD_APP_HOME_PATH
  if (pathname === '/visits' || pathname === '/visits/') return '/facilities'
  if (canStayOnAppHost(pathname, viewerRole)) return null
  if (isFieldAppAllowedPath(pathname)) return null
  const target = normalAppUrlForPath(pathname, search, hash, hostname, protocol, fallbackOrigin)
  // Never full-page replace to the current origin+path (infinite reload).
  try {
    const currentOrigin = fallbackOrigin || `${protocol}//${hostname}`
    const current = `${currentOrigin}${safePath(pathname)}${search}${hash}`
    if (target === current) return null
  } catch {
    // fall through
  }
  return target
}
